// Single implementation of "never let a credential in a URL reach an
// analytics vendor", shared by the PostHog `before_send` redaction
// (posthogExceptionFilter.ts), the Faro `beforeSend` redaction
// (faroRedaction.ts) and the GTM mount guard (GTMGuarded.tsx), so all three
// agree on what counts as sensitive.
// cspell:ignore Fverify Ftoken

// URL parameter names whose values must NEVER reach a third party, in the
// query string OR the fragment. The claim/opt-out/confirm magic-link `token`
// is the canonical case (#3841): autocapture / RUM fire their first events on
// init, *before* a page's mount effect strips the token from the URL. The
// newsletter confirm link carries it in the fragment (`#token=`, #12323).
export const SENSITIVE_QUERY_PARAMS: readonly string[] = ['token'];

const REDACTED = 'REDACTED';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// `%(?:25)*` matches a percent-encoded character at any encoding depth
// (`%3F`, `%253F`, ...), so a token carried inside another parameter's value
// (e.g. claim/verify's `returnTo=%2Fverify%3Ftoken%3D...`) is caught too.
// Delimiters before the name: `?`, `&`, `#` and their encodings
// (3F, 26, 23).
const PARAM_NAMES = SENSITIVE_QUERY_PARAMS.map(escapeRegExp).join('|');
const SENSITIVE_PARAM_PATTERN = new RegExp(
  `((?:[?&#]|%(?:25)*(?:3F|26|23))(?:${PARAM_NAMES}))` + // delimiter + name
    `(=|%(?:25)*3D)` + // separator
    `(?:(?!%(?:25)*(?:26|23))[^&#\\s"'])*`, // value, up to a (possibly encoded) delimiter
  'gi'
);

// Cheap pre-check: a string that does not contain any sensitive name cannot
// match, so the full pattern is skipped (this runs on every analytics string).
// No `g` flag, so `.test` keeps no `lastIndex` state between calls.
const SENSITIVE_NAME_HINT = new RegExp(PARAM_NAMES, 'i');

/**
 * Replace the value of every sensitive URL parameter in any string with
 * `REDACTED` - in the query or the fragment (`?token=x`, `&token=x`,
 * `#token=x`) and percent-encoded at any depth (`%3Ftoken%3Dx`,
 * `%23token%3Dx`, `%253Ftoken%253Dx`). Works on absolute URLs, relative paths
 * and free text embedding a URL; other parameters and the rest of the string
 * are untouched.
 */
export function redactSensitiveParamsInString(value: string): string {
  if (!SENSITIVE_NAME_HINT.test(value)) return value;
  return value.replace(SENSITIVE_PARAM_PATTERN, `$1$2${REDACTED}`);
}

/** True when `value` carries a sensitive URL parameter (the one shared rule). */
export function carriesSensitiveParam(value: string): boolean {
  return redactSensitiveParamsInString(value) !== value;
}

/**
 * How a copy rewrites one string. `key` is the property the string sits
 * under (null for an array element or the root), so a caller can apply a
 * key-specific rule (PostHog anonymises UUIDs in `*pathname` paths).
 */
type StringTransform = (value: string, key: string | null) => string;

const redactOnly: StringTransform = (value) =>
  redactSensitiveParamsInString(value);

/**
 * A node the redaction cannot copy safely. The analytics hooks catch it (and
 * any error raised while reading the payload) and DROP the event: fail
 * closed, never send it unredacted.
 */
export class RedactionRefusedError extends Error {
  constructor(reason: string) {
    super(`URL redaction cannot copy this payload: ${reason}`);
    this.name = 'RedactionRefusedError';
  }
}

type Container = Record<string, unknown>;

/** Plain data the redaction copies: arrays and plain / null-prototype objects. */
function isPlainContainer(node: object): boolean {
  if (Array.isArray(node)) return true;
  const proto = Object.getPrototypeOf(node);
  return proto === Object.prototype || proto === null;
}

function emptyLike(node: object): Container {
  if (Array.isArray(node)) return [] as unknown as Container;
  return Object.getPrototypeOf(node) === null ? Object.create(null) : {};
}

/**
 * Read-only: would `transform` change any string JSON serialization of
 * `root` emits? Follows what `JSON.stringify` follows (`toJSON()` and
 * enumerable own properties, recursively) through plain and non-plain
 * objects alike.
 */
function treeNeedsChange(root: unknown, transform: StringTransform): boolean {
  const seen = new WeakSet<object>();
  const stack: Array<[unknown, string | null]> = [[root, null]];
  for (let next = stack.pop(); next; next = stack.pop()) {
    const [node, key] = next;
    if (typeof node === 'string' && transform(node, key) !== node) return true;
    if (typeof node !== 'object' || node === null || seen.has(node)) continue;
    seen.add(node);
    pushSerializedChildren(node, stack, key);
  }
  return false;
}

/** Queue what `JSON.stringify` would descend into, with each value's key. */
function pushSerializedChildren(
  node: object,
  stack: Array<[unknown, string | null]>,
  key: string | null
): void {
  const toJSON = (node as { toJSON?: unknown }).toJSON;
  if (typeof toJSON === 'function') stack.push([toJSON.call(node), key]);
  const isArray = Array.isArray(node);
  for (const child of Object.keys(node)) {
    stack.push([(node as Container)[child], isArray ? null : child]);
  }
}

/**
 * Return the data tree with every string passed through `transform`
 * (default: redact sensitive URL params), WITHOUT writing to the input:
 *
 * - A read-only scan runs first. When no string would change, the input
 *   itself is returned (no copy for the common, clean event).
 * - Otherwise plain objects and arrays are copied, with the transformed
 *   strings in the copy; a frozen or caller-owned input is fine.
 * - Non-plain objects (Error, Date, class instances) are not cloned: kept by
 *   reference when nothing they would serialize changes, otherwise the whole
 *   payload is refused with `RedactionRefusedError` (the caller drops it).
 *
 * There is deliberately NO depth limit: OTLP trace payloads nest `http.url`
 * nine levels down (`resourceSpans[].scopeSpans[].spans[].attributes[].value
 * .stringValue`) and PostHog nests copies of the entry URL
 * (`$initial_person_info.u`, `$set_once.$initial_current_url`). Both walks
 * are iterative (no stack overflow); a source -> copy map keeps cycles and
 * shared references intact.
 */
export function redactedCopy<T>(
  root: T,
  transform: StringTransform = redactOnly
): T {
  if (!treeNeedsChange(root, transform)) return root;
  const copier = new RedactingCopier(transform);
  const out = copier.resolve(root, null);
  copier.drain();
  return out as T;
}

/** The state of one `redactedCopy` run: source -> copy map and the work queue. */
class RedactingCopier {
  private readonly copies = new Map<object, unknown>();
  private readonly queue: Array<[Container, Container]> = [];

  constructor(private readonly transform: StringTransform) {}

  /** The value to put in the copy for `value` (copying plain containers lazily). */
  resolve(value: unknown, key: string | null): unknown {
    if (typeof value === 'string') return this.transform(value, key);
    if (typeof value !== 'object' || value === null) return value;
    if (this.copies.has(value)) return this.copies.get(value);
    if (!isPlainContainer(value)) return this.keepNonPlain(value);
    const copy = emptyLike(value);
    this.copies.set(value, copy);
    this.queue.push([value as Container, copy]);
    return copy;
  }

  /** Fill every queued copy from its source; iterative, so no depth limit. */
  drain(): void {
    for (let next = this.queue.pop(); next; next = this.queue.pop()) {
      const [source, copy] = next;
      const isArray = Array.isArray(source);
      for (const key of Object.keys(source)) {
        // defineProperty, not assignment: an own `__proto__` key (JSON.parse
        // makes those) must stay data, never reset the copy's prototype.
        Object.defineProperty(copy, key, {
          value: this.resolve(source[key], isArray ? null : key),
          enumerable: true,
          writable: true,
          configurable: true,
        });
      }
    }
  }

  private keepNonPlain(value: object): object {
    if (treeNeedsChange(value, this.transform)) {
      throw new RedactionRefusedError(
        'a non-plain object carries a sensitive URL parameter'
      );
    }
    this.copies.set(value, value);
    return value;
  }
}
